import { ComponentType, ReactNode } from "react";

export type ForwardingType = 'immediate' | 'no-answer';
export type Carrier =
  | 'Verizon'
  | 'AT&T'
  | 'T-Mobile'
  | 'Sprint'
  | 'Cricket Wireless'
  | 'Metro by T-Mobile'
  | 'Boost Mobile';

export const carrierInstructions: Record<
  Carrier,
  { immediate: string[]; 'no-answer': string[] }
> = {
  Verizon: {
    immediate: [
      'Call *72 from your Verizon phone',
      'Enter your Greet phone number when prompted',
      'Press # to confirm',
      'To disable: Call *73 from your phone',
    ],
    'no-answer': [
      'Call *90 from your Verizon phone',
      'Enter your Greet phone number when prompted',
      'Press # to confirm',
      'To disable: Call *91 from your phone',
    ],
  },
  'AT&T': {
    immediate: [
      'Call *21* from your AT&T phone',
      'Enter your Greet phone number',
      'Press # to confirm',
      'To disable: Call #21# from your phone',
    ],
    'no-answer': [
      'Call *61* from your AT&T phone',
      'Enter your Greet phone number',
      'Press # to confirm',
      'To disable: Call #61# from your phone',
    ],
  },
  'T-Mobile': {
    immediate: [
      'Call 611 from your T-Mobile phone',
      'Ask to set up immediate call forwarding',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
    'no-answer': [
      'Call 611 from your T-Mobile phone',
      'Ask to set up call forwarding when no answer',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
  },
  Sprint: {
    immediate: [
      'Call *72 from your Sprint phone',
      'Enter your Greet phone number when prompted',
      'Press # to confirm',
      'To disable: Call *73 from your phone',
    ],
    'no-answer': [
      'Call *90 from your Sprint phone',
      'Enter your Greet phone number when prompted',
      'Press # to confirm',
      'To disable: Call *91 from your phone',
    ],
  },
  'Cricket Wireless': {
    immediate: [
      'Call 611 from your Cricket phone',
      'Ask to set up immediate call forwarding',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
    'no-answer': [
      'Call 611 from your Cricket phone',
      'Ask to set up call forwarding when no answer',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
  },
  'Metro by T-Mobile': {
    immediate: [
      'Call 611 from your Metro phone',
      'Ask to set up immediate call forwarding',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
    'no-answer': [
      'Call 611 from your Metro phone',
      'Ask to set up call forwarding when no answer',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
  },
  'Boost Mobile': {
    immediate: [
      'Call 611 from your Boost phone',
      'Ask to set up immediate call forwarding',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
    'no-answer': [
      'Call 611 from your Boost phone',
      'Ask to set up call forwarding when no answer',
      'Provide your Greet phone number',
      'To disable: Call 611 and ask to remove call forwarding',
    ],
  },
};

export const carriers: Carrier[] = [
  'Verizon',
  'AT&T',
  'T-Mobile',
  'Sprint',
  'Cricket Wireless',
  'Metro by T-Mobile',
  'Boost Mobile',
];

export type SpecialArticleId = 'interactive-forwarding-tool' | 'sms-summaries-setup';

export interface HelpArticle {
  title: string;
  slug: string;
  description: string;
  content: ReactNode | SpecialArticleId;
}

export interface HelpCategory {
  id: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  articles: HelpArticle[];
}

export interface HelpArticleWithCategory extends HelpArticle {
  categoryId: string;
  categoryTitle: string;
  categoryIcon: ComponentType<{ className?: string }>;
}

const GETTING_STARTED_CONTENT = {
  setupAiReceptionist: (
    <>
      <p>Configure your AI receptionist in a few steps. After signing up, go to your dashboard and open the AI agent settings.</p>
      
      <p>Add your business name, a short summary of what you do, and your main phone agent prompt (AI should answer and what information to give). Include business hours, services, pricing, and common questions so the AI can answer accurately.</p>
      
      <p>You can use a dedicated Greet number or forward your existing business number to Greet. Once your number is set and the agent is configured, the AI will answer incoming calls.</p>
    </>
  ),
  firstPhoneNumber: (
    <>
      <p>You can either get a new Greet phone number or use your existing business number with call forwarding.</p>
      
      <p><strong>New number:</strong> In the app, go to Phone Numbers and purchase or claim a number. Use this as your main business line and add it to your website, Google Business, and marketing.</p>
      
      <p><strong>Existing number:</strong> Set up call forwarding from your current carrier to your Greet number so callers still dial your familiar number but Greet answers. See the <a href="/help-center/call-forwarding-setup">Call Forwarding Setup</a> article in Phone Number & Call Forwarding for carrier-specific steps.</p>
    </>
  ),
  customizingFaq: (
    <>
      <p>Your AI agent prompt is where you define how the AI answers. Include:</p>
      
      <ul>
        <li>Business hours and time zone</li>
        <li>Location and address</li>
        <li>List of services with brief descriptions</li>
        <li>Pricing or price ranges if you share them publicly</li>
        <li>Answers to common questions (insurance, cancellations, booking)</li>
        <li>Any policies (cancellation, refunds)</li>
      </ul>
      
      <p>The more accurate and up-to-date this information is, the better the AI can serve callers. You can edit the prompt anytime from your dashboard.</p>
    </>
  ),
};

const TRY_GREET_CONTENT = {
  whatIsTry: (
    <>
      <p>Try lets you create a temporary AI phone agent in under a minute using only your website address. No sign-up or credit card required.</p>
      
      <p>Enter your business URL on the Try page; Greet scans your site and extracts business name, services, pricing, hours, FAQ, and contact info, then generates a phone agent configuration. You get a demo number to call and test how your business would sound.</p>
    </>
  ),
  whatInfoIsPulled: (
    <>
      <p>When creating your AI Phone Agent, you may optionally have Greet scan your website to extract information like business name, services, pricing, hours, FAQ, and contact info.</p>

      <p>Greet looks for:</p>
      
      <ul>
        <li>Business name and type</li>
        <li>Services with descriptions</li>
        <li>Pricing</li>
        <li>Business hours and location</li>
        <li>FAQ or common questions</li>
        <li>Contact information</li>
        <li>Unique selling points</li>
        <li>Policies (cancellation, refunds)</li>
      </ul>
      
      <p>Not all sites have every type of content-Greet uses whatever it finds to build the best possible agent.</p>
    </>
  ),
};

const PHONE_SETUP_CONTENT = {
  interactiveForwarding: 'interactive-forwarding-tool' as const,
};

const FEATURES_CONTENT = {
  transcripts: (
    <>
      <p>You may configure Greet to transcrib and recorded calls, the transcript is saved in your dashboard and sent to your email (if email notifications are enabled). Transcripts include what the caller said and how the AI responded.</p>
      
      <p>Use them to spot trends, improve your agent prompt, train staff, and follow up on leads. You can search and filter call logs by date, duration, and outcome. You can also opt-in to SMS notifications for each call.</p>
      
      <p>For clients who require HIPAA and similar compliance with privacy laws, Greet will not transcribe calls without explicit consent from the caller.</p>
    </>
  ),
  callPlayback: (
    <>
      <p>Recordings and transcripts are available in your dashboard under Call Logs. Open a call to see the full transcript and listen to the recording if enabled for your account.</p>
      
      <p>Reviewing calls helps you refine your AI agent prompt, handle follow-ups, and ensure quality. Recordings may be subject to your plan and local consent laws.</p>
    </>
  ),
  smsSummaries: 'sms-summaries-setup' as const,
  calendarScheduling: (
    <>
      <p>Connect your Google Calendar so the AI can offer real availability and book appointments. To configure scheduling, go to <a href="/app/scheduling">Scheduling</a> and connect Greet with your Google Calendar. Set your availability and buffer times.</p>
      
      <p>Next open your Greet AI agent and enable scheduling, the AI Agent will now be able to suggest times and create events. Appointments appear in your calendar and in the Greet dashboard. Keep your calendar updated so the AI doesn't offer times you're not available.</p>
    </>
  ),
  voiceSelection: (
    <>
      <p>You can choose the voice your AI agent uses. In your dashboard, edit your AI agent and look for the voice or sound settings. Pick a voice that fits your brand-professional, friendly, or neutral.</p>
      
      <p>Voice options may vary by plan. Changes apply to new calls immediately.</p>
    </>
  ),
};

const SPAM_PROTECTION_CONTENT = {
  automaticDetection: (
    <>
      <p>Greet automatically protects you from spam calls using an industry-leading database of over 10.5 million known spam numbers. This protection works in real-time for all incoming calls with zero configuration required.</p>
      
      <p>When a spam call comes in, it's automatically detected and logged in your call history for your records. However, you won't receive any email or SMS notifications for these calls, so you can focus on legitimate customer inquiries without interruption.</p>
      
      <p>The spam database is continuously updated to ensure you're protected against the latest spam campaigns. All of this happens automatically in the background-you don't need to do anything to enable or maintain this protection.</p>
    </>
  ),
  manualBlocking: (
    <>
      <p>You can manually block any phone number at any time, giving you complete control over which calls trigger notifications. There are two ways to block a number:</p>
      
      <p><strong>From the Spam Detection page:</strong> Navigate to the <a href="/app/spam-detection">Spam Detection</a> section in your dashboard, enter the phone number you want to block, and optionally add a note explaining why you're blocking it (for your own reference).</p>
      
      <p><strong>From Call History:</strong> When viewing your call logs, you can block any number directly from the call record. This is convenient when you receive an unwanted call and want to immediately prevent future notifications from that number.</p>
      
      <p>When you block a number, calls from that number will still appear in your call logs (for record-keeping and compliance), but you won't receive any email or SMS notifications about them.</p>
    </>
  ),
  managingBlocked: (
    <>
      <p>All your blocked numbers are visible in the <a href="/app/spam-detection">Spam Detection</a> page under "Manually Blocked Numbers."</p>
      
      <p>You can unblock any number at any time if you blocked it by mistake or if circumstances change. Simply click the "Unblock" button next to the number, confirm your choice, and you'll start receiving notifications for calls from that number again.</p>
      
      <p>The blocked numbers list helps you keep track of which numbers you've manually blocked and why, making it easy to review and manage your spam protection settings over time.</p>
    </>
  ),
};

const OPTIMIZATION_CONTENT = {
  maximizingCallQuality: (
    <>
      <p>Your AI receptionist gets smarter with every call. Review your call transcripts weekly to identify common questions and add them to your agent prompt. The more specific information you provide about your business, services, and policies, the more accurately your AI can respond to customers.</p>
      
      <p><strong>Pro tip:</strong> Listen to your first 5-10 calls and note any questions the AI handles well or could improve on. Use these insights to refine your agent prompt with exact phrasing your customers use.</p>
    </>
  ),
  businessListings: (
    <>
      <p>Getting calls from the right customers starts with consistent business information. Update these key platforms with your Greet number:</p>
      
      <ul>
        <li>Your website (especially contact pages and footers)</li>
        <li>Google Business Profile</li>
        <li>Facebook and Instagram business pages</li>
        <li>Industry directories like Yelp or Yellow Pages</li>
        <li>Email signatures</li>
      </ul>
      
      <p>When you update your number, keep the format consistent (e.g., (415) 555-0123) across all platforms. This builds trust and makes it easier for customers to recognize your business.</p>
    </>
  ),
  faqBestPractices: (
    <>
      <p>The secret to a great AI receptionist is a comprehensive FAQ. Structure your agent prompt to include:</p>
      
      <ul>
        <li>Exact business hours with time zone</li>
        <li>Complete service descriptions with pricing ranges</li>
        <li>Booking and cancellation policies</li>
        <li>Payment methods accepted</li>
        <li>Service area or delivery zones</li>
        <li>Common objections with responses</li>
      </ul>
      
      <p>Think about the last 10 customer questions you answered on the phone-those should all be in your FAQ. The AI handles these perfectly when you give it the right information upfront.</p>
    </>
  ),
  testingSetup: (
    <>
      <p>Before going live, test your AI receptionist from a friend's phone or different number. Call during and outside business hours to check the responses. Try asking about pricing, availability, booking an appointment, and general business questions. Listen for natural conversation flow and accurate information.</p>
      
      <p>After your first real calls, check the transcripts immediately. Look for:</p>
      
      <ul>
        <li>Did the AI capture customer needs?</li>
        <li>Were responses accurate?</li>
        <li>Did conversations feel natural?</li>
      </ul>
      
      <p>Use this feedback to fine-tune your agent prompt within the first 48 hours.</p>
    </>
  ),
};

export function getHelpCategories(icons: {
  BookOpenIcon: ComponentType<{ className?: string }>;
  ChatBubbleLeftRightIcon: ComponentType<{ className?: string }>;
  DocumentTextIcon: ComponentType<{ className?: string }>;
  QuestionMarkCircleIcon: ComponentType<{ className?: string }>;
  PhoneIcon: ComponentType<{ className?: string }>;
  SparklesIcon: ComponentType<{ className?: string }>;
  UserCircleIcon: ComponentType<{ className?: string }>;
}): HelpCategory[] {
  return [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: icons.BookOpenIcon,
      articles: [
        { 
          title: 'How to set up your AI receptionist', 
          slug: 'setup-ai-receptionist',
          description: 'Learn how to configure your AI receptionist with business details, phone agent prompts, and essential settings to start answering calls.',
          content: GETTING_STARTED_CONTENT.setupAiReceptionist 
        },
        { 
          title: 'Setting up your first phone number', 
          slug: 'setup-first-phone-number',
          description: 'Get a new Greet phone number or use your existing business number with call forwarding to start receiving calls.',
          content: GETTING_STARTED_CONTENT.firstPhoneNumber 
        },
        { 
          title: 'Customizing your FAQ responses', 
          slug: 'customize-faq-responses',
          description: 'Define how your AI answers calls by customizing your agent prompt with business hours, services, pricing, and common questions.',
          content: GETTING_STARTED_CONTENT.customizingFaq 
        },
      ],
    },
    {
      id: 'try-greet',
      title: 'Try Greet',
      icon: icons.SparklesIcon,
      articles: [
        { 
          title: 'What is Try and how does it work?', 
          slug: 'what-is-try',
          description: 'Create a temporary AI phone agent in under a minute using just your website address. No sign-up or credit card required.',
          content: TRY_GREET_CONTENT.whatIsTry 
        },
        { 
          title: 'What information does Greet pull from my website?', 
          slug: 'website-information-extraction',
          description: 'Discover what business information Greet automatically extracts from your website to configure your AI phone agent.',
          content: TRY_GREET_CONTENT.whatInfoIsPulled 
        },
      ],
    },
    {
      id: 'phone-setup',
      title: 'Phone Number & Call Forwarding',
      icon: icons.PhoneIcon,
      articles: [
        { 
          title: 'Call Forwarding Setup', 
          slug: 'call-forwarding-setup',
          description: 'Step-by-step instructions for setting up call forwarding with your carrier. Choose immediate or no-answer forwarding with carrier-specific codes.',
          content: PHONE_SETUP_CONTENT.interactiveForwarding 
        },
      ],
    },
    {
      id: 'features',
      title: 'Features & Functionality',
      icon: icons.ChatBubbleLeftRightIcon,
      articles: [
        { 
          title: 'Understanding call transcripts', 
          slug: 'call-transcripts',
          description: 'Learn how call transcripts are generated, saved, and sent to your email to help you track conversations and improve your AI agent.',
          content: FEATURES_CONTENT.transcripts 
        },
        { 
          title: 'Call playback and recordings', 
          slug: 'call-playback-recordings',
          description: 'Access and listen to call recordings in your dashboard to review AI performance and follow up on customer conversations.',
          content: FEATURES_CONTENT.callPlayback 
        },
        { 
          title: 'SMS summaries and notifications', 
          slug: 'sms-summaries-notifications',
          description: 'Receive instant text message summaries of every call with key information and next steps. Learn how to enable and manage SMS notifications.',
          content: FEATURES_CONTENT.smsSummaries 
        },
        { 
          title: 'Calendar and appointment scheduling', 
          slug: 'calendar-scheduling',
          description: 'Connect your Google Calendar to let your AI agent book appointments based on your real-time availability.',
          content: FEATURES_CONTENT.calendarScheduling 
        },
        { 
          title: 'Choosing your AI voice', 
          slug: 'choose-ai-voice',
          description: 'Select the perfect voice for your AI receptionist to match your brand personality and create the right first impression.',
          content: FEATURES_CONTENT.voiceSelection 
        },
        { 
          title: 'Understanding automatic spam detection', 
          slug: 'automatic-spam-detection',
          description: 'Discover how Greet automatically protects you from spam calls using a database of over 10.5 million known spam numbers.',
          content: SPAM_PROTECTION_CONTENT.automaticDetection 
        },
        { 
          title: 'Manually blocking phone numbers', 
          slug: 'manually-block-numbers',
          description: 'Learn how to manually block unwanted phone numbers from your call history or spam detection page to prevent future notifications.',
          content: SPAM_PROTECTION_CONTENT.manualBlocking 
        },
        { 
          title: 'Managing your blocked numbers list', 
          slug: 'manage-blocked-numbers',
          description: 'View, manage, and unblock phone numbers from your spam protection settings to control which calls trigger notifications.',
          content: SPAM_PROTECTION_CONTENT.managingBlocked 
        },
      ],
    },
    {
      id: 'optimization',
      title: 'Optimization & Best Practices',
      icon: icons.SparklesIcon,
      articles: [
        { 
          title: 'Maximizing your AI call quality', 
          slug: 'maximize-call-quality',
          description: 'Learn how to refine your AI agent using call transcripts and customer feedback to provide even better service.',
          content: OPTIMIZATION_CONTENT.maximizingCallQuality 
        },
        { 
          title: 'Where to add your Greet number', 
          slug: 'business-listings-setup',
          description: 'Strategic guide to updating your phone number across all customer touchpoints for maximum visibility.',
          content: OPTIMIZATION_CONTENT.businessListings 
        },
        { 
          title: 'Writing effective FAQs', 
          slug: 'faq-best-practices',
          description: 'Create a comprehensive knowledge base that helps your AI receptionist handle any customer question confidently.',
          content: OPTIMIZATION_CONTENT.faqBestPractices 
        },
        { 
          title: 'Testing your setup before going live', 
          slug: 'testing-setup',
          description: 'Quick testing checklist to ensure your AI receptionist delivers perfect customer experiences from day one.',
          content: OPTIMIZATION_CONTENT.testingSetup 
        },
      ],
    },
  ];
}

/** Returns help article slugs for sitemap and other static generation. */
export function getHelpArticleSlugs(): string[] {
  const dummyIcon = () => null;
  const categories = getHelpCategories({
    BookOpenIcon: dummyIcon,
    ChatBubbleLeftRightIcon: dummyIcon,
    DocumentTextIcon: dummyIcon,
    QuestionMarkCircleIcon: dummyIcon,
    PhoneIcon: dummyIcon,
    SparklesIcon: dummyIcon,
    UserCircleIcon: dummyIcon,
  });
  return categories.flatMap((c) => c.articles.map((a) => a.slug));
}

// Helper function to get all articles with their category information
export function getAllArticles(icons: {
  BookOpenIcon: ComponentType<{ className?: string }>;
  ChatBubbleLeftRightIcon: ComponentType<{ className?: string }>;
  DocumentTextIcon: ComponentType<{ className?: string }>;
  QuestionMarkCircleIcon: ComponentType<{ className?: string }>;
  PhoneIcon: ComponentType<{ className?: string }>;
  SparklesIcon: ComponentType<{ className?: string }>;
  UserCircleIcon: ComponentType<{ className?: string }>;
}): HelpArticleWithCategory[] {
  const categories = getHelpCategories(icons);
  const articles: HelpArticleWithCategory[] = [];
  
  categories.forEach((category) => {
    category.articles.forEach((article) => {
      articles.push({
        ...article,
        categoryId: category.id,
        categoryTitle: category.title,
        categoryIcon: category.icon,
      });
    });
  });
  
  return articles;
}

// Helper function to get a single article by slug
export function getArticleBySlug(
  slug: string,
  icons: {
    BookOpenIcon: ComponentType<{ className?: string }>;
    ChatBubbleLeftRightIcon: ComponentType<{ className?: string }>;
    DocumentTextIcon: ComponentType<{ className?: string }>;
    QuestionMarkCircleIcon: ComponentType<{ className?: string }>;
    PhoneIcon: ComponentType<{ className?: string }>;
    SparklesIcon: ComponentType<{ className?: string }>;
    UserCircleIcon: ComponentType<{ className?: string }>;
  }
): HelpArticleWithCategory | undefined {
  const allArticles = getAllArticles(icons);
  return allArticles.find((article) => article.slug === slug);
}

// Helper function to get related articles (from same category, or popular ones)
export function getRelatedArticles(
  currentSlug: string,
  icons: {
    BookOpenIcon: ComponentType<{ className?: string }>;
    ChatBubbleLeftRightIcon: ComponentType<{ className?: string }>;
    DocumentTextIcon: ComponentType<{ className?: string }>;
    QuestionMarkCircleIcon: ComponentType<{ className?: string }>;
    PhoneIcon: ComponentType<{ className?: string }>;
    SparklesIcon: ComponentType<{ className?: string }>;
    UserCircleIcon: ComponentType<{ className?: string }>;
  },
  limit: number = 3
): HelpArticleWithCategory[] {
  const allArticles = getAllArticles(icons);
  const currentArticle = allArticles.find((a) => a.slug === currentSlug);
  
  if (!currentArticle) {
    return allArticles.slice(0, limit);
  }
  
  // First, try to get articles from the same category
  const sameCategoryArticles = allArticles.filter(
    (article) => 
      article.categoryId === currentArticle.categoryId && 
      article.slug !== currentSlug
  );
  
  // If we have enough from the same category, return them
  if (sameCategoryArticles.length >= limit) {
    return sameCategoryArticles.slice(0, limit);
  }
  
  // Otherwise, get other articles to fill up to the limit
  const otherArticles = allArticles.filter(
    (article) => 
      article.categoryId !== currentArticle.categoryId
  );
  
  return [
    ...sameCategoryArticles,
    ...otherArticles.slice(0, limit - sameCategoryArticles.length)
  ];
}
